import { normalizeFreeTextHtml, snapFontSizes } from "./freeTextConfig";

// 블록(Editor.js) 본문 → FreeText HTML 변환
// 사용자가 편집 화면에서 "FreeText로 전환" 을 누를 때만 쓴다 (저장된 글을 자동으로 바꾸지 않음).
// 결과는 FreeText 스키마(createFreeTextExtensions)로 한 번 더 정리되므로 스키마에 없는 서식은 빠진다.

interface Block {
  type: string;
  data?: any;
}

export interface BlockConversionResult {
  html: string;
  // 변환하지 못하고 건너뛴 블록 종류 (예: ["embed"])
  skippedTypes: string[];
}

// 블록 본문 문자열(JSON)에서 블록 목록을 꺼낸다
export const parseBlocks = (content?: string | null): Block[] => {
  if (!content) return [];
  try {
    const parsed = JSON.parse(content);
    return Array.isArray(parsed?.blocks) ? parsed.blocks : [];
  } catch {
    return [];
  }
};

const escapeHtml = (text: string): string =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

// 블록 안 글자는 이미 인라인 HTML (굵게, 링크, 색 등) 이다
const inline = (value: unknown): string => (typeof value === "string" ? value : "");

// 일반 글자(코드 블록 등)는 이스케이프하고 줄바꿈을 <br> 로
const plainLines = (value: unknown): string =>
  escapeHtml(typeof value === "string" ? value : "").replace(/\r?\n/g, "<br>");

// 목록: 예전 형식(문자열 배열)과 새 형식({ content, items } 중첩) 둘 다 받는다
const listItems = (items: unknown[], tag: "ul" | "ol"): string =>
  `<${tag}>${items
    .map((item: any) => {
      if (typeof item === "string") return `<li><p>${item}</p></li>`;
      const children = Array.isArray(item?.items) && item.items.length > 0 ? listItems(item.items, tag) : "";
      return `<li><p>${inline(item?.content)}</p>${children}</li>`;
    })
    .join("")}</${tag}>`;

const isSafeUrl = (url: unknown): url is string => typeof url === "string" && /^(https?:\/\/|\/(?!\/))/i.test(url);

const image = (url: unknown, caption: unknown): string => {
  if (!isSafeUrl(url)) return "";
  const captionHtml = inline(caption).trim();
  return `<img src="${escapeHtml(url)}">${captionHtml ? `<p style="text-align: center">${captionHtml}</p>` : ""}`;
};

const link = (url: unknown, label: unknown): string => {
  const text = typeof label === "string" && label.trim() ? label : typeof url === "string" ? url : "";
  // 주소가 아닌 값은 글자로만 남긴다
  if (!isSafeUrl(url)) return text ? `<p>${escapeHtml(text)}</p>` : "";
  return `<p><a href="${escapeHtml(url)}">${escapeHtml(text)}</a></p>`;
};

const convertBlock = (block: Block): string | null => {
  const data = block.data ?? {};
  switch (block.type) {
    case "paragraph":
      return `<p>${inline(data.text)}</p>`;
    case "header": {
      const level = Math.min(Math.max(Number(data.level) || 2, 1), 4);
      return `<h${level}>${inline(data.text)}</h${level}>`;
    }
    case "list":
      return listItems(Array.isArray(data.items) ? data.items : [], data.style === "ordered" ? "ol" : "ul");
    case "checklist":
      // FreeText 에는 체크 목록이 없어 기호로 체크 여부를 남긴다
      return `<ul>${(Array.isArray(data.items) ? data.items : [])
        .map((item: any) => `<li><p>${item?.checked ? "☑" : "☐"} ${inline(item?.text)}</p></li>`)
        .join("")}</ul>`;
    case "table": {
      const rows: unknown[][] = Array.isArray(data.content) ? data.content : [];
      if (rows.length === 0) return "";
      return `<table><tbody>${rows
        .map((row, rowIndex) => {
          const cellTag = data.withHeadings && rowIndex === 0 ? "th" : "td";
          const cells = Array.isArray(row) ? row : [];
          return `<tr>${cells.map((cell) => `<${cellTag}><p>${inline(cell)}</p></${cellTag}>`).join("")}</tr>`;
        })
        .join("")}</tbody></table>`;
    }
    case "image":
      return image(data.file?.url, data.caption);
    case "simpleImage":
      return image(data.url, data.caption);
    case "delimiter":
      return "<hr>";
    case "quote":
      return `<blockquote><p>${inline(data.text)}</p>${
        inline(data.caption).trim() ? `<p>${inline(data.caption)}</p>` : ""
      }</blockquote>`;
    case "warning":
      return `<blockquote><p><strong>${inline(data.title)}</strong></p><p>${inline(data.message)}</p></blockquote>`;
    case "code":
      return `<p>${plainLines(data.code)}</p>`;
    case "linkTool":
      return link(data.link, data.meta?.title);
    case "embed":
      return link(data.source, data.caption);
    default:
      return null;
  }
};

export const convertBlocksToFreeText = (content?: string | null): BlockConversionResult => {
  const skipped = new Set<string>();
  const html = parseBlocks(content)
    .map((block) => {
      const converted = convertBlock(block);
      if (converted === null) skipped.add(block.type);
      return converted ?? "";
    })
    .join("");

  // 블록 편집기의 배경색은 <mark> 로 저장되어 있어 FreeText 배경색(<span style>) 으로 바꾼다
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.body.querySelectorAll("mark").forEach((mark) => {
    const span = doc.createElement("span");
    const style = mark.getAttribute("style");
    if (style) span.setAttribute("style", style);
    span.append(...Array.from(mark.childNodes));
    mark.replaceWith(span);
  });
  snapFontSizes(doc.body);

  // 스키마에 없는 스타일만 있던 <span> 은 속성 없이 남으므로 풀어 준다
  const normalized = new DOMParser().parseFromString(normalizeFreeTextHtml(doc.body.innerHTML), "text/html");
  normalized.body.querySelectorAll("span:not([style])").forEach((span) => span.replaceWith(...Array.from(span.childNodes)));

  return { html: normalized.body.innerHTML, skippedTypes: Array.from(skipped) };
};
